<script lang="ts">
  import Icon from './Icon.svelte';
  /**
   * Chapter editor for audio/video materials. Renders a vertical list of
   * timecode rows (start time + title + optional end + remove ×) plus an
   * "+ Додати таймкод" button at the bottom.
   *
   * Times are stored as integer seconds (matching the Bunny chapter wire
   * format) but edited as MM:SS strings — humans don't think in raw seconds.
   * On change we re-sort by start so rows shown in order without admin
   * needing to drag.
   */
  import { onDestroy, onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import type { MaterialChapter } from '../lib/types';

  interface Props {
    chapters: MaterialChapter[];
    onChange: (next: MaterialChapter[]) => void;
    /** Total media duration in seconds — used to validate that chapter times
     *  don't run past the end of the video/audio. Optional because we may
     *  load it slightly later than the chapters themselves. */
    duration?: number | null;
    /** Notify parent when validation state flips so they can disable Save. */
    onValidityChange?: (valid: boolean) => void;
    /** Material id — required when the "Generate via AI" button is shown.
     *  When omitted the button is hidden (e.g., on the New material page
     *  before the row exists). */
    materialId?: string;
  }
  let { chapters, onChange, duration = null, onValidityChange, materialId }: Props = $props();

  /** Convert "MM:SS" or "H:MM:SS" → seconds. Returns null on garbage input. */
  function parseTime(s: string): number | null {
    const trimmed = s.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(':').map((p) => p.trim());
    if (parts.length < 2 || parts.length > 3) return null;
    const nums = parts.map(Number);
    if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
    if (parts.length === 2) return nums[0] * 60 + nums[1];
    return nums[0] * 3600 + nums[1] * 60 + nums[2];
  }

  /** seconds → "MM:SS" (or "H:MM:SS" past an hour). */
  function formatTime(sec: number): string {
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
  }

  // Local edit state — each row owns its time string so typing doesn't snap
  // back to the formatted value until the field blurs. We sort & emit on
  // blur, not on every keystroke, to keep cursor stable while admin types.
  type Row = {
    title: string;
    startStr: string;
    endStr: string;
  };
  function toRow(c: MaterialChapter): Row {
    return {
      title: c.title,
      startStr: formatTime(c.start),
      endStr: c.end !== undefined ? formatTime(c.end) : '',
    };
  }

  // svelte-ignore state_referenced_locally — навмисно: ресинк робить $effect
  // нижче, і лише коли проп справді змінився (інакше затирав би набране).
  let rows = $state<Row[]>(chapters.map(toRow));

  // Re-seed local rows ONLY when the parent prop actually changes (initial
  // load, sync from Bunny). The previous version depended on `rows` too and
  // fired on every keystroke, clobbering admin's typing with the not-yet-
  // committed parent state. Tracking a JSON snapshot of `chapters` alone
  // means this effect only runs on prop changes.
  // svelte-ignore state_referenced_locally — знімок для порівняння, див. нижче.
  let lastChaptersJson = JSON.stringify(chapters);
  $effect(() => {
    const incomingJson = JSON.stringify(chapters);
    if (incomingJson !== lastChaptersJson) {
      lastChaptersJson = incomingJson;
      rows = chapters.map(toRow);
    }
  });

  /** Build a valid MaterialChapter[] from current rows, dropping rows with
   *  invalid times or empty titles. Sorted by start. */
  function rowsToChapters(rs: Row[]): MaterialChapter[] {
    const out: MaterialChapter[] = [];
    for (const r of rs) {
      const title = r.title.trim();
      const start = parseTime(r.startStr);
      if (!title || start === null) continue;
      const end = parseTime(r.endStr);
      const ch: MaterialChapter = { title, start };
      if (end !== null && end > start) ch.end = end;
      out.push(ch);
    }
    return out.sort((a, b) => a.start - b.start);
  }

  function commit(): void {
    onChange(rowsToChapters(rows));
  }

  function addRow(): void {
    // New chapter's start defaults to the latest time used by any existing
    // chapter (max of all start AND end values) so admin never accidentally
    // adds an overlapping row — the natural continuation of the previous one.
    let maxTime = 0;
    for (const r of rows) {
      const s = parseTime(r.startStr);
      if (s !== null && s > maxTime) maxTime = s;
      const e = parseTime(r.endStr);
      if (e !== null && e > maxTime) maxTime = e;
    }
    rows = [...rows, { title: '', startStr: formatTime(maxTime), endStr: '' }];
    // Don't commit yet — empty title would be dropped. Wait for admin to fill in.
  }

  function removeRow(idx: number): void {
    rows = rows.filter((_, i) => i !== idx);
    commit();
  }

  /** Re-validate on every keystroke. Returns an array of human-readable
   *  messages — each one referencing a row index so admin sees which timecode
   *  is wrong. Empty array = everything checks out. */
  const errors = $derived.by((): string[] => {
    const out: string[] = [];
    const parsedStarts: (number | null)[] = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const n = i + 1;
      const title = r.title.trim();
      const start = parseTime(r.startStr);
      const end = parseTime(r.endStr);
      parsedStarts.push(start);

      if (!title) out.push(`Таймкод ${n}: додай назву`);
      if (r.startStr.trim() === '' || start === null) {
        out.push(`Таймкод ${n}: некоректний час початку (формат MM:SS)`);
      } else if (duration && start > duration) {
        out.push(
          `Таймкод ${n}: початок (${formatTime(start)}) виходить за межі матеріалу (${formatTime(duration)})`,
        );
      }
      if (r.endStr.trim() !== '') {
        if (end === null) {
          out.push(`Таймкод ${n}: некоректний час кінця (формат MM:SS)`);
        } else {
          if (start !== null && end <= start) {
            out.push(`Таймкод ${n}: кінець має бути більше за початок`);
          }
          if (duration && end > duration) {
            out.push(
              `Таймкод ${n}: кінець (${formatTime(end)}) виходить за межі матеріалу (${formatTime(duration)})`,
            );
          }
        }
      }
    }
    // Cross-row check: starts must be unique. Duplicate starts mean two
    // chapters claim the same moment — Bunny rejects, admin probably
    // meant something else.
    const seen = new Map<number, number>();
    for (let i = 0; i < parsedStarts.length; i++) {
      const s = parsedStarts[i];
      if (s === null) continue;
      const prev = seen.get(s);
      if (prev !== undefined) {
        out.push(`Таймкоди ${prev + 1} і ${i + 1} починаються в одну й ту саму мить`);
      } else {
        seen.set(s, i);
      }
    }
    // Cross-row check: overlapping ranges. Sort by start, then walk pairs and
    // flag any case where the previous end runs past the next start. Only
    // checks rows where both start and end are parseable — missing ends get
    // auto-filled at save time so they can't overlap.
    const indexed = rows.map((r, i) => ({
      i,
      start: parseTime(r.startStr),
      end: parseTime(r.endStr),
    }));
    const sorted = indexed
      .filter((x): x is { i: number; start: number; end: number | null } => x.start !== null)
      .sort((a, b) => a.start - b.start);
    for (let k = 1; k < sorted.length; k++) {
      const prev = sorted[k - 1];
      const cur = sorted[k];
      if (prev.end !== null && prev.end > cur.start) {
        out.push(`Таймкоди ${prev.i + 1} і ${cur.i + 1} перетинаються`);
      }
    }
    return out;
  });

  // Bubble validity to the parent so the Save button can be gated on it.
  $effect(() => {
    onValidityChange?.(errors.length === 0);
  });

  /** Helpers that present MM:SS as two separate fields with a visible colon
   *  between them. Splitting on ':' keeps the existing startStr/endStr shape
   *  unchanged — we just project it for display and re-join on input. */
  function mmOf(s: string): string {
    const parts = s.split(':');
    return parts[0] ?? '';
  }
  function ssOf(s: string): string {
    const parts = s.split(':');
    return parts[1] ?? '';
  }
  function digitsOnly(v: string, max: number): string {
    return v.replace(/\D/g, '').slice(0, max);
  }

  // === Bunny AI chapter generation =========================================
  // Paid Bunny feature — POST /transcribe with GenerateChapters=true queues
  // a job that takes 1-5 minutes. We poll every 8s for up to 7 minutes;
  // when chapters appear in Bunny we sync them locally and stop polling.
  let generating = $state(false);
  let genError = $state<string | null>(null);
  let pollHandle: ReturnType<typeof setTimeout> | null = null;
  let pollAttempts = 0;
  const POLL_INTERVAL_MS = 8000;
  const POLL_MAX_ATTEMPTS = 53; // ≈ 7 minutes

  // Cost + balance shown on the button. Fetched once on mount; null balance
  // means the server doesn't have BUNNY_ACCOUNT_API_KEY configured.
  let accountInfo = $state<{
    balance: number | null;
    currency: string;
    transcribeUsdPerMinute: number;
  } | null>(null);
  const estimatedCost = $derived(
    accountInfo && duration
      ? Math.round((duration / 60) * accountInfo.transcribeUsdPerMinute * 100) / 100
      : null,
  );

  function fmtMoney(v: number, currency = 'USD'): string {
    const sym = currency === 'USD' ? '$' : `${currency} `;
    return `${sym}${v.toFixed(2)}`;
  }

  onMount(async () => {
    if (!materialId) return;
    try {
      accountInfo = await api.adminBunnyAccountInfo();
    } catch {
      // Account info is decorative — failure shouldn't block generation.
    }
  });

  onDestroy(() => {
    if (pollHandle !== null) clearTimeout(pollHandle);
  });

  async function generate(): Promise<void> {
    if (!materialId || generating) return;
    generating = true;
    genError = null;
    pollAttempts = 0;
    try {
      await api.adminGenerateChapters(materialId, 'uk');
      pollHandle = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      generating = false;
      if (err instanceof ApiError) {
        if (err.status === 409) genError = 'У матеріалу вже є таймкоди — генерацію пропущено.';
        else genError = `Bunny відмовила (${err.code}). Перевір що матеріал має відео-файл і спробуй знову.`;
      } else {
        genError = 'Не вдалось запустити генерацію. Перевір з\'єднання.';
      }
    }
  }

  async function poll(): Promise<void> {
    if (!materialId) return;
    pollAttempts += 1;
    try {
      const status = await api.adminBunnyChapterStatus(materialId);
      if (status.chapters.length > 0) {
        // Bunny finished — push chapters up so the editor renders them.
        onChange(status.chapters);
        generating = false;
        return;
      }
    } catch {
      // Network blip — keep polling
    }
    if (pollAttempts >= POLL_MAX_ATTEMPTS) {
      generating = false;
      genError =
        'Bunny ще не повернув таймкоди (>7 хв). Залиш сторінку відкритою — годинна синхронізація підтягне їх автоматично.';
      return;
    }
    pollHandle = setTimeout(poll, POLL_INTERVAL_MS);
  }

  /** Resize a textarea to fit its content. Bound to oninput for live updates
   *  and to use:autoGrow so the initial render already has the right height. */
  function grow(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }
  function autoGrow(el: HTMLTextAreaElement): void {
    // requestAnimationFrame so we measure after the browser has rendered the
    // bound value into the textarea — otherwise scrollHeight is still 0.
    requestAnimationFrame(() => grow(el));
  }
</script>

<div class="chapters">
  {#if rows.length === 0}
    <div class="chapters-empty">Немає таймкодів. Додай перший нижче.</div>
    {#if materialId}
      <button
        type="button"
        class="ai-btn"
        onclick={generate}
        disabled={generating}
      >
        {#if generating}
          <span class="ai-spinner"></span>
          Bunny обробляє відео… (1-5 хв)
        {:else}
          <span class="ai-btn-label"><Icon name="sparkles" /> Згенерувати через AI</span>
          {#if estimatedCost !== null || accountInfo?.balance !== null}
            <span class="ai-btn-meta">
              {#if estimatedCost !== null}≈{fmtMoney(estimatedCost, accountInfo?.currency)}{/if}
              {#if estimatedCost !== null && accountInfo?.balance !== null} · {/if}
              {#if accountInfo?.balance !== null && accountInfo}залиш. {fmtMoney(accountInfo.balance, accountInfo.currency)}{/if}
            </span>
          {/if}
        {/if}
      </button>
      {#if generating}
        <div class="ai-notice">
          <Icon name="triangle-alert" size="xs" /> Не закривай цю сторінку — генерація триває. Якщо закриєш, прогрес не загубиться, але доведеться повернутись сюди й оновити сторінку, щоб побачити готові таймкоди.
        </div>
      {/if}
      {#if genError}
        <div class="ai-error">{genError}</div>
      {/if}
    {/if}
  {/if}
  {#each rows as row, i (i)}
    <div class="chapter-row">
      <div class="time-group">
        <input
          class="time-seg"
          type="text"
          inputmode="numeric"
          maxlength="3"
          placeholder="00"
          value={mmOf(row.startStr)}
          oninput={(e) => {
            row.startStr = `${digitsOnly(e.currentTarget.value, 3)}:${ssOf(row.startStr)}`;
          }}
          onblur={commit}
        />
        <span class="time-colon">:</span>
        <input
          class="time-seg"
          type="text"
          inputmode="numeric"
          maxlength="2"
          placeholder="00"
          value={ssOf(row.startStr)}
          oninput={(e) => {
            row.startStr = `${mmOf(row.startStr)}:${digitsOnly(e.currentTarget.value, 2)}`;
          }}
          onblur={commit}
        />
      </div>
      <span class="dash">→</span>
      <div class="time-group">
        <input
          class="time-seg"
          type="text"
          inputmode="numeric"
          maxlength="3"
          placeholder="00"
          value={mmOf(row.endStr)}
          oninput={(e) => {
            row.endStr = `${digitsOnly(e.currentTarget.value, 3)}:${ssOf(row.endStr)}`;
          }}
          onblur={commit}
        />
        <span class="time-colon">:</span>
        <input
          class="time-seg"
          type="text"
          inputmode="numeric"
          maxlength="2"
          placeholder="00"
          value={ssOf(row.endStr)}
          oninput={(e) => {
            row.endStr = `${mmOf(row.endStr)}:${digitsOnly(e.currentTarget.value, 2)}`;
          }}
          onblur={commit}
        />
      </div>
      <!-- Textarea instead of input so long titles wrap onto multiple lines
       *   and admin sees the whole thing without scrolling sideways.
       *   `use:autoGrow` fires once on mount AND on every input — keeps
       *   height in sync with content for both existing and newly-typed text. -->
      <textarea
        class="title-input"
        rows="1"
        maxlength="200"
        placeholder="Назва таймкоду"
        bind:value={row.title}
        onblur={commit}
        oninput={(e) => grow(e.currentTarget)}
        use:autoGrow
      ></textarea>
      <button
        type="button"
        class="row-remove"
        onclick={() => removeRow(i)}
        aria-label="Видалити таймкод"
        title="Видалити"
      >×</button>
    </div>
  {/each}
  <button
    type="button"
    class="add-btn"
    onclick={addRow}
    disabled={generating}
    title={generating ? 'Зачекай — AI ще генерує таймкоди' : ''}
  >+ Додати таймкод</button>

  {#if errors.length > 0}
    <div class="chapter-errors">
      <div class="chapter-errors-title">Перевір таймкоди:</div>
      <ul>
        {#each errors as e (e)}<li>{e}</li>{/each}
      </ul>
    </div>
  {/if}
</div>

<style>
  .chapters {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 14px;
  }
  .chapters-empty {
    padding: 12px 14px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
    color: var(--text-muted);
    font-size: 12px;
    line-height: 1.5;
  }
  /* AI generate button — purple gradient since this is a paid feature; we
   * want it visually prominent vs the regular "+ Додати" dashed button. */
  .ai-btn {
    width: 100%;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    border: none;
    padding: 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    transition: filter 0.15s;
  }
  .ai-btn-label {
    font-size: 13px;
  }
  .ai-btn-meta {
    font-size: 11px;
    font-weight: 500;
    opacity: 0.85;
  }
  .ai-btn:hover:not(:disabled) {
    filter: brightness(1.08);
  }
  .ai-btn:disabled {
    opacity: 0.7;
    cursor: progress;
  }
  .ai-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(251, 246, 236, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: ai-spin 0.8s linear infinite;
  }
  @keyframes ai-spin { to { transform: rotate(360deg); } }
  .ai-error {
    padding: 10px 14px;
    background: rgba(196, 99, 62, 0.08);
    border: 1px solid rgba(196, 99, 62, 0.3);
    border-radius: 10px;
    color: var(--status-inactive);
    font-size: 12px;
    line-height: 1.45;
  }
  .ai-notice {
    padding: 10px 14px;
    background: rgba(232, 161, 61, 0.08);
    border: 1px solid rgba(232, 161, 61, 0.35);
    border-radius: 10px;
    color: var(--status-expiring);
    font-size: 12px;
    line-height: 1.5;
  }
  .add-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .add-btn:disabled:hover {
    border-color: var(--border2);
    color: var(--text-muted);
    background: var(--bg2);
  }
  .chapter-row {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 6px 8px;
  }
  /* Split time field: [MM] : [SS] — visible colon is always between the two
   * digit inputs so admin only types numbers (no need to ever press ':').
   * Two inputs share a single rounded border for a "one field" look. */
  .time-group {
    display: inline-flex;
    align-items: center;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 0 2px;
    flex-shrink: 0;
  }
  .time-group:focus-within {
    border-color: var(--outline);
  }
  .time-seg {
    width: 36px;
    padding: 6px 4px;
    background: transparent;
    border: none;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: var(--text);
    text-align: center;
  }
  .time-seg:focus {
    outline: none;
  }
  .time-seg::placeholder {
    color: var(--text-muted);
    opacity: 0.5;
  }
  .time-colon {
    color: var(--text-muted);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    pointer-events: none;
    user-select: none;
  }
  .dash {
    color: var(--text-muted);
    font-size: 12px;
    flex-shrink: 0;
  }
  /* Single-line by default (rows=1), grows on input — see the inline JS
   * handlers in the markup. Resize disabled because the auto-grow drives
   * height; admin shouldn't override it manually. */
  .title-input {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 13px;
    color: var(--text);
    font-family: inherit;
    line-height: 1.4;
    resize: none;
    overflow: hidden;
    /* Match the time-group height when content fits on one line */
    min-height: 32px;
  }
  .title-input:focus {
    outline: none;
    border-color: var(--outline);
  }
  .row-remove {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 18px;
    line-height: 1;
    padding: 4px 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.15s;
    flex-shrink: 0;
  }
  .row-remove:hover {
    background: rgba(196, 99, 62, 0.12);
    color: var(--heart);
  }
  .add-btn {
    width: 100%;
    background: var(--bg2);
    border: 1.5px dashed var(--border2);
    color: var(--text-muted);
    padding: 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
  }
  .add-btn:hover {
    border-color: var(--outline);
    color: var(--accent-text);
    background: var(--pu-light);
  }
  /* Live validation summary — appears whenever rows fail any rule.
   * Updates on every keystroke via $derived. */
  .chapter-errors {
    padding: 10px 14px;
    background: rgba(196, 99, 62, 0.08);
    border: 1px solid rgba(196, 99, 62, 0.3);
    border-radius: 10px;
  }
  .chapter-errors-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--heart);
    margin-bottom: 4px;
  }
  .chapter-errors ul {
    margin: 0;
    padding-left: 18px;
    list-style: disc;
  }
  .chapter-errors li {
    font-size: 12px;
    color: var(--status-inactive);
    line-height: 1.45;
    margin: 2px 0;
  }
</style>
